/* Source-aligned tabs and native-scroll carousels, shared by home and cart rails. */
(() => {
  'use strict';
  if (window.WingsCarousel) return;
  const epsilon = 1;
  const focusable = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,summary,audio[controls],video[controls],[contenteditable]:not([contenteditable="false"]),[tabindex]:not([tabindex="-1"])';
  const carousels = new Map();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const direction = (track) => getComputedStyle(track).direction === 'rtl' ? -1 : 1;
  const maximum = (track) => Math.max(0, track.scrollWidth - track.clientWidth);
  const position = (track) => Math.min(maximum(track), Math.abs(track.scrollLeft));
  const padding = (value, basis) => {
    const amount = parseFloat(value);
    return Number.isFinite(amount) ? (value.trim().endsWith('%') ? amount * basis / 100 : amount) : 0;
  };
  function metrics(track) {
    const rtl = direction(track) === -1;
    const style = getComputedStyle(track);
    const bounds = track.getBoundingClientRect();
    const pos = position(track);
    const view = track.clientWidth;
    const origin = rtl ? bounds.right - (parseFloat(style.borderRightWidth) || 0) : bounds.left + (parseFloat(style.borderLeftWidth) || 0);
    return {
      slides: [...track.children].map((child) => {
        const box = child.getBoundingClientRect();
        const start = (rtl ? origin - box.right : box.left - origin) + pos;
        return {start, end: start + box.width};
      }),
      pos, view, max: maximum(track),
      padStart: padding(style.scrollPaddingInlineStart || '0px', view),
      padEnd: padding(style.scrollPaddingInlineEnd || '0px', view),
    };
  }
  function nextOffset({slides, pos, max, view, padStart, padEnd}) {
    const usable = Math.max(1, view - padStart - padEnd);
    const cut = slides.find((slide) => slide.end > pos + view - padEnd + epsilon);
    let target = cut ? cut.start - padStart : pos + usable;
    if (target <= pos + epsilon) target = pos + usable;
    return Math.min(max, target);
  }
  function previousOffset({slides, pos, view, padStart, padEnd}) {
    const usable = Math.max(1, view - padStart - padEnd);
    const first = slides.findIndex((slide) => slide.start >= pos + padStart - epsilon);
    if (first === 0) return 0;
    let target = pos - usable;
    if (first > 0) {
      const lastToShow = slides[first - 1];
      const from = slides.find((slide) => lastToShow.end - slide.start <= usable + epsilon);
      if (from) target = from.start - padStart;
    }
    if (target >= pos - epsilon) target = pos - usable;
    return Math.max(0, target);
  }
  const scrollTo = (track, offset, behavior) => track.scrollTo({left: direction(track) * Math.min(maximum(track), Math.max(0, offset)), behavior});
  const find = (root, suffix) => root.querySelector(`[data-carousel-${suffix}],[data-home-${suffix}]`);
  function initialiseCarousel(carousel) {
    if (carousels.has(carousel)) return;
    const track = find(carousel, 'track');
    if (!track) return;
    const previous = find(carousel, 'prev');
    const next = find(carousel, 'next');
    const controls = find(carousel, 'controls') || previous?.parentElement;
    const controller = new AbortController();
    const listenerOptions = {signal: controller.signal};
    if (!track.id) track.id = `WingsCarouselTrack-${carousels.size}-${Math.random().toString(36).slice(2, 8)}`;
    carousel.setAttribute('role', 'region');
    carousel.setAttribute('aria-roledescription', 'carousel');
    if (!carousel.hasAttribute('aria-label') && !carousel.hasAttribute('aria-labelledby')) {
      const title = carousel.closest('section')?.querySelector('h2[id],h3[id]');
      if (title) carousel.setAttribute('aria-labelledby', title.id);
    }
    [previous, next].forEach((button) => {
      if (!button) return;
      button.disabled = false;
      button.setAttribute('aria-controls', track.id);
    });
    const update = () => {
      const {pos, max} = metrics(track);
      const overflow = max > epsilon;
      carousel.dataset.carouselReady = 'true';
      carousel.dataset.carouselOverflow = String(overflow);
      if (controls) controls.setAttribute('aria-hidden', String(!overflow));
      previous?.setAttribute('aria-disabled', String(pos <= epsilon));
      next?.setAttribute('aria-disabled', String(pos >= max - epsilon));
      const shouldFocus = overflow && track.querySelector(focusable) === null;
      if (shouldFocus && track.tabIndex !== 0) track.tabIndex = 0;
      else if (!shouldFocus && track.hasAttribute('tabindex')) track.removeAttribute('tabindex');
      const label = carousel.dataset.slideLabel;
      [...track.children].forEach((slide, index, all) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        if (label) slide.setAttribute('aria-label', label.replace(/\{index\}|\[\[index\]\]/g, String(index + 1)).replace(/\{total\}|\[\[total\]\]/g, String(all.length)));
      });
    };
    const page = (towards) => {
      const measured = metrics(track);
      if ((towards < 0 && measured.pos <= epsilon) || (towards > 0 && measured.pos >= measured.max - epsilon)) return;
      scrollTo(track, towards < 0 ? previousOffset(measured) : nextOffset(measured), reducedMotion() ? 'instant' : 'smooth');
    };
    previous?.addEventListener('click', () => page(-1), listenerOptions);
    next?.addEventListener('click', () => page(1), listenerOptions);
    track.addEventListener('scroll', update, {...listenerOptions, passive: true});
    carousel.addEventListener('wings:refresh', update, listenerOptions);
    // Touch and pen keep the browser's native scrolling. Mouse drags preserve plain clicks.
    let gesture = null;
    let suppressClick = false;
    let clickTimer;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,textarea,summary,dialog')) return;
      gesture = {id: event.pointerId, x: event.clientX, y: event.clientY, pos: position(track), dragging: false};
    }, listenerOptions);
    track.addEventListener('pointermove', (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.dragging) {
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return;
        gesture.dragging = true;
        track.dataset.dragging = 'true';
        track.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      scrollTo(track, gesture.pos - dx * direction(track), 'instant');
    }, listenerOptions);
    const stopDrag = (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      if (gesture.dragging) {
        suppressClick = true;
        clearTimeout(clickTimer);
        clickTimer = setTimeout(() => { suppressClick = false; }, 400);
      }
      if (track.hasPointerCapture?.(event.pointerId)) track.releasePointerCapture(event.pointerId);
      delete track.dataset.dragging;
      gesture = null;
    };
    track.addEventListener('pointerup', stopDrag, listenerOptions);
    window.addEventListener('pointerup', stopDrag, listenerOptions);
    track.addEventListener('pointercancel', stopDrag, listenerOptions);
    track.addEventListener('lostpointercapture', stopDrag, listenerOptions);
    track.addEventListener('dragstart', (event) => { if (gesture) event.preventDefault(); }, listenerOptions);
    track.addEventListener('click', (event) => {
      if (!suppressClick || event.detail === 0) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, {...listenerOptions, capture: true});
    let resizeObserver;
    let mutationObserver;
    const observe = () => {
      resizeObserver?.observe(track);
      [...track.children].forEach((slide) => resizeObserver?.observe(slide));
    };
    if ('ResizeObserver' in window) { resizeObserver = new ResizeObserver(update); observe(); }
    else window.addEventListener('resize', update, listenerOptions);
    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(() => { observe(); update(); });
      mutationObserver.observe(track, {childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'tabindex', 'disabled', 'contenteditable']});
    }
    carousels.set(carousel, () => { controller.abort(); resizeObserver?.disconnect(); mutationObserver?.disconnect(); clearTimeout(clickTimer); delete track.dataset.dragging; });
    update();
  }
  function initialize(root = document) {
    for (const [carousel, cleanup] of carousels) if (!carousel.isConnected) { cleanup(); carousels.delete(carousel); }
    root.querySelectorAll('[data-home-tabs]').forEach((section) => {
      if (section.dataset.tabsInitialized) return;
      section.dataset.tabsInitialized = 'true';
      const tabs = [...section.querySelectorAll('[data-home-tab]')];
      function select(tab, focus = false) {
        for (const candidate of tabs) {
          const active = candidate === tab;
          candidate.setAttribute('aria-selected', String(active));
          candidate.tabIndex = active ? 0 : -1;
          const panel = document.getElementById(candidate.getAttribute('aria-controls'));
          if (panel) panel.hidden = !active;
        }
        const shop = section.querySelector('[data-home-shop]');
        if (shop && tab.dataset.href) shop.href = tab.dataset.href;
        if (focus) tab.focus();
        section.querySelectorAll('[data-home-carousel],[data-wings-carousel]').forEach((rail) => rail.dispatchEvent(new Event('wings:refresh')));
      }
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
          const rtl = getComputedStyle(section).direction === 'rtl';
          let next = index;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
          else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
          else return;
          event.preventDefault();
          select(tabs[(next + tabs.length) % tabs.length], true);
        });
      });
      if (tabs[0]) select(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
    const containers = [...root.querySelectorAll('[data-home-carousel],[data-wings-carousel]')];
    if (root.matches?.('[data-home-carousel],[data-wings-carousel]')) containers.unshift(root);
    containers.forEach(initialiseCarousel);
  }
  window.WingsCarousel = {init: initialize};
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [carousel, cleanup] of carousels) if (event.target.contains(carousel)) { cleanup(); carousels.delete(carousel); }
  });
  document.addEventListener('wings:cart-updated', () => initialize());
})();
